import { useRef } from 'react'
import { Download, Upload } from 'lucide-react'

interface UploadSectionProps {
    uploadProgress: number | null
    isDragging: boolean
    error: string | null
    isMobile: boolean
    onFileUpload: (file: File) => void
    onDragStateChange: (isDragging: boolean) => void
}

export function UploadSection({
    uploadProgress,
    isDragging,
    error,
    isMobile,
    onFileUpload,
    onDragStateChange
}: UploadSectionProps) {
    const fileInputRef = useRef<HTMLInputElement>(null)

    return (
        <section className="h-full flex flex-col px-6 py-6 bg-gradient-to-r from-blue-500/5 to-transparent">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-400" />
                    Ingestão
                </h3>
                <span className="text-xs text-gray-500 font-medium">Step 1 of 3</span>
            </div>

            {/* Upload Zone - Takes remaining space */}
            <div className="flex-1 flex flex-col min-h-0">
                <div
                    className={`flex-1 border-2 border-dashed rounded-2xl 
                               transition-all cursor-pointer
                               flex flex-col items-center justify-center gap-4
                               ${isDragging
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-white/20 hover:border-blue-500/50 bg-white/5 hover:bg-white/10'
                        }`}
                    onDragOver={(e) => { e.preventDefault(); onDragStateChange(true); }}
                    onDragLeave={() => onDragStateChange(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        onDragStateChange(false);
                        const file = e.dataTransfer.files[0];
                        if (file) onFileUpload(file);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.txt,.md"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) onFileUpload(file);
                        }}
                        className="hidden"
                    />

                    {uploadProgress !== null ? (
                        <div className="w-full max-w-xs px-4 space-y-3">
                            <div className="flex justify-between text-sm text-blue-300 font-medium">
                                <span>Uploading...</span>
                                <span>{uploadProgress}%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2">
                                <div
                                    className="bg-blue-500 h-2 rounded-full transition-all"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <Download className="w-6 h-6 text-blue-400" />
                            </div>
                            <div className="text-center px-4">
                                <p className="text-gray-300 font-medium">
                                    {isMobile ? 'Tap to upload' : 'Drag & Drop'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    PDF, TXT, MD (5MB)
                                </p>
                            </div>

                            {/* Dataset Selector */}
                            <select
                                onClick={(e) => e.stopPropagation()}
                                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-gray-300 cursor-pointer hover:bg-white/15 transition-colors"
                            >
                                <option>default</option>
                                <option>technical</option>
                                <option>legal</option>
                            </select>
                        </>
                    )}
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm flex-shrink-0">
                        {error}
                    </div>
                )}
            </div>
        </section>
    )
}
